import React, { useState, useEffect, useMemo } from 'react';
import { FormControlLabel, Switch, Box, Stack, Typography, Paper } from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { useAppSelector } from '../../../app/hooks';
import { Company } from '../../../interfaces/users.interface';
import { Workflow } from '../../../interfaces/workflow.interface';
import { getCompanyReferences } from '../../../services/companyDetail';
import AssessmentCard from './AssessmentCard';
import AssessmentForm from '../../Assessments/NewAssessmentModal';
import { COMPANY_TYPES } from '../../../services/vendor';
import { DEFAULT_PER_PAGE } from '../../Vendors/companyTabUi';
import { useSectionPager } from '../../Vendors/sectionPager';

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    nonCompliant: true;
  }
}

type AssessmentsTabProps = {
  company: Company;
  userCanAdminCompany: boolean;
  assessmentModalOpen?: boolean;
  onAssessmentModalClose?: () => void;
};

const AssessmentsTab = (props: AssessmentsTabProps) => {
  const { company, userCanAdminCompany, assessmentModalOpen: externalAssessmentModalOpen, onAssessmentModalClose } = props;
  const { user } = useAppSelector((state) => state.user);
  const { workflows } = useAppSelector(state => state.workflow);

  const [companies, setCompanies] = useState<Company[]>([]);
  const [showCompleted, setShowCompleted] = useState(false);
  const [internalAssessmentModalOpen, setInternalAssessmentModalOpen] = useState(false);
  const [filteredWorkflows, setFilteredWorkflows] = useState<Workflow[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PER_PAGE);

  // AssessmentCard resolves each survey's site/app from data embedded on the
  // survey (assessment fetch), so no company-wide sites/apps fetch is needed.

  const assessmentModalOpen = externalAssessmentModalOpen !== undefined ? externalAssessmentModalOpen : internalAssessmentModalOpen;

  const handleAssessmentModalClose = () => {
    if (onAssessmentModalClose) {
      onAssessmentModalClose();
    } else {
      setInternalAssessmentModalOpen(false);
    }
  };

  const activeWorkflows = useMemo(() => workflows?.filter(cw => cw.isActive && cw.assessment), [workflows]);

  const listFilteredWorkflows = useMemo(
    () => {
      const singleAssessmentFlows = filteredWorkflows.filter((value, index, self) => {
        return self.findIndex(v => v.assessment === value.assessment) === index;
      });

      singleAssessmentFlows.sort((a, b) => {
        return (b.assessment || 0) - (a.assessment || 0);
      });

      return showCompleted
        ? singleAssessmentFlows
        : singleAssessmentFlows.filter((wf) => {
          if (company.type === COMPANY_TYPES.ASSESSOR)
            return ['Remediation', 'Completed', 'Complete'].indexOf(wf.status) === -1;
          if (company.type === COMPANY_TYPES.VENDOR) return ['Completed', 'Complete'].indexOf(wf.status) === -1;
        });
    }, [showCompleted, filteredWorkflows],
  );

  const totalPages = Math.max(1, Math.ceil(listFilteredWorkflows.length / pageSize));
  useSectionPager({ count: listFilteredWorkflows.length, page, pageSize, setPage, setPageSize });
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);
  const pagedWorkflows = useMemo(
    () => listFilteredWorkflows.slice((page - 1) * pageSize, page * pageSize),
    [listFilteredWorkflows, page, pageSize],
  );

  useEffect(() => {
    if (!activeWorkflows) return;
    const nWorkflows = activeWorkflows.filter((wf) => {
      if (company.type === COMPANY_TYPES.ASSESSOR) {
        if (wf?.assignedAssessor === user?.id && wf?.status !== 'Rejected') return true;
        return wf?.assessor === company.id && wf?.status !== 'Rejected';
      }
      return true;
    });
    setFilteredWorkflows(nWorkflows ? nWorkflows : []);
  }, [company, activeWorkflows, user?.id]);

  useEffect(() => {
    if (!filteredWorkflows || filteredWorkflows.length === 0 || company?.type !== COMPANY_TYPES.ASSESSOR) return;
    // Only the assessed vendors' identity (id/name/type/published) is needed here
    // — fetch lightweight references in one call rather than each full company.
    const companyIds = [...new Set(filteredWorkflows.map((wf) => wf.serviceProvider))];
    getCompanyReferences(companyIds).then((refs) => setCompanies(refs as unknown as Company[]));
  }, [filteredWorkflows, company.type]);

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      {/* Header bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography variant='body2' color='text.secondary'>
            {listFilteredWorkflows.length} {listFilteredWorkflows.length === 1 ? 'assessment' : 'assessments'}
          </Typography>
        </Box>
        <FormControlLabel
          control={<Switch checked={showCompleted} onChange={(e) => setShowCompleted(e.target.checked)} />}
          label='Show Completed'
        />
      </Box>

      {/* Empty state */}
      {listFilteredWorkflows.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            p: 6,
            textAlign: 'center',
          }}
        >
          <AssessmentIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant='h6' sx={{ mb: 0.5 }}>
            No assessments
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {showCompleted
              ? 'No assessments have been created yet.'
              : 'No active assessments. Toggle "Show Completed" to see all assessments.'}
          </Typography>
        </Paper>
      ) : (
        <Box>
          {pagedWorkflows.map((wf: Workflow) => (
            <AssessmentCard
              key={wf.id}
              company={company}
              vendorCompany={
                company.type === COMPANY_TYPES.ASSESSOR && companies.length > 0
                  ? companies.find((comp) => comp.id === wf.serviceProvider)
                  : undefined
              }
              workflow={wf}
              userCanAdminCompany={userCanAdminCompany}
            />
          ))}
        </Box>
      )}

      <AssessmentForm
        modalOpen={assessmentModalOpen}
        company={company}
        closeCallback={handleAssessmentModalClose}
      />
    </Stack>
  );
};

export default AssessmentsTab;
